<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { useVuelidate } from '@vuelidate/core'
import { helpers, requiredIf } from '@vuelidate/validators'

import type {
	IFetchApiChatFile,
	IFetchApiChatMessageAdd,
	IFetchApiChatMessageFile,
	IFetchApiChatNoteAdd
} from '~/portal/api'
import {
	CPButton,
	CPCheckbox,
	CPFileUpload,
	CPTextEditor
} from '~/portal/components/base'
import type { ICPChatItemFile } from '~/portal/components/pages'
import { CPChatType } from '~/portal/components/pages'
import { AccessScopesEnum, useAccessToken, useAuthData, useSnackbar } from '~/portal/composables'
import { useChatStore } from '~/portal/stores'
import { fileToBase64, getErrors } from '~/portal/utils'

const props = withDefaults(
	defineProps<{
		noteId?: string | number
		messageId?: string | number
		editText?: string | null
		editFiles?: ICPChatItemFile[]
		projectId: string | number
		type?: CPChatType
		disabled?: boolean
	}>(), {
		type: CPChatType?.Default,
		disabled: false
	}
)

const chatStore = useChatStore()
const { checkHasScope } = useAuthData()

const { token } = useAccessToken()

const { showSnackbar } = useSnackbar()

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'cancel'): void
}>()

const {
	fetchChatNoteUpdate,
	fetchChatMessageEdit,
	fetchApiChatNoteAdd,
	fetchChatMessageAdd
} = chatStore

interface IServerFile {
	id?: number
	file_key: string
	name: string
}

type IReplyFile = File | IServerFile

const files = ref<IReplyFile[]>()
const editor = ref()
const isSending = ref(false)
const isSentToEmail = ref(false)
const message = ref('')

const isEditMode = computed<boolean>(() => (
	Boolean(props.noteId || props.messageId)
))

const isFileEditable = computed<boolean>(() => !props.noteId)

const uploadKey = computed<string>(() => (
	`${props.noteId || ''}-${props.messageId || ''}`
))

const rules = computed(() => {
	const hasFiles = Boolean(files.value?.length)

	return {
		message: {
			requiredIf: helpers.withMessage(
				'Dieses Feld darf nicht leer sein',
				requiredIf(!hasFiles)
			)
		}
	}
})

const v$ = useVuelidate(rules, { message })

const buttonLabel = computed(() => {
	if (props.messageId) return 'Speichern'
	return props.noteId ? 'Bearbeiten' : 'Schicken'
})

const editSuccessText = computed<string>(() => (
	props.messageId
		? 'Nachricht erfolgreich bearbeitet'
		: 'Notiz erfolgreich bearbeitet'
))

const showSendEmail = computed(() => (
	props.type === CPChatType.Default && !isEditMode.value && checkHasScope([
		AccessScopesEnum.PORTAL_TEAM_LEAD,
		AccessScopesEnum.PORTAL_PROJECT_MANAGER,
		AccessScopesEnum.PORTAL_EMPLOYEE
	])
))

watch(
	(): (string | number | undefined)[] => [props.noteId, props.messageId],
	(ids: (string | number | undefined)[]): void => {
		if (!ids[0] && !ids[1]) {
			clearForm()
			return
		}

		message.value = props.editText || ''
		files.value = (props.editFiles || []).map(
			(file: ICPChatItemFile): IServerFile => ({
				id: file.id,
				file_key: file.key,
				name: file.name
			})
		)
		v$.value.$reset()
		editor.value?.editor?.commands?.focus()
	}
)

const clearForm = (): void => {
	message.value = ''
	isSentToEmail.value = false
	files.value = undefined
	v$.value.$reset()
}

const updateNote = async (): Promise<void> => {
	if (!props.noteId) {
		throw new Error('Missing required value: noteId in updateNote')
	}

	return await fetchChatNoteUpdate({
		token: token.value,
		text: message.value,
		projectId: props.projectId,
		noteId: props.noteId
	})
}

const getServerFileId = (file: IReplyFile): number | undefined => {
	const meta = (file as { __serverMeta__?: { id?: number } }).__serverMeta__
	return meta?.id || (file as IServerFile).id
}

const getEditFileList = async (): Promise<IFetchApiChatMessageFile[]> => {
	const list = files.value || []

	const toEntry = async (
		file: IReplyFile
	): Promise<IFetchApiChatMessageFile> => {
		const id = getServerFileId(file)
		if (id) {
			return { id }
		}

		const base64 = await fileToBase64(file as File)

		return {
			name: (file as File).name,
			base64: base64 || undefined
		}
	}

	return await Promise.all(list.map(toEntry))
}

const updateMessage = async (): Promise<void> => {
	if (!props.messageId) {
		throw new Error('Missing required value: messageId in updateMessage')
	}

	return await fetchChatMessageEdit({
		token: token.value,
		text: message.value,
		projectId: props.projectId,
		messageId: props.messageId,
		files: await getEditFileList()
	})
}

const getFileList = async (value?: File[]): Promise<IFetchApiChatFile[]> => {
	if (!value) {
		return []
	}

	const list: Promise<string | null>[] = value.map(async (item) => {
		return await fileToBase64(item)
	})

	const values = await Promise.all(list)

	return values.map((item, index) => {
		return {
			name: value[index].name,
			base64: item?.replace(/^data:.+;base64,/, '') || undefined
		}
	}) as IFetchApiChatFile[]
}

const addMessage = async (): Promise<void> => {
	const fileFormatted = await getFileList(files.value as File[])

	if (props.type === CPChatType.Default) {
		const payload: IFetchApiChatMessageAdd = {
			token: token.value,
			text: message.value,
			projectId: props.projectId,
			emailNotify: isSentToEmail.value
		}

		if (fileFormatted?.length) {
			payload.files = fileFormatted
		}

		return await fetchChatMessageAdd(payload)
	} else {
		const payload: IFetchApiChatNoteAdd = {
			token: token.value,
			projectId: props.projectId,
			text: message.value
		}

		if (fileFormatted?.length) {
			payload.files = fileFormatted
		}

		return await fetchApiChatNoteAdd(payload)
	}
}

const sendMessage = async (): Promise<void> => {
	if (props.messageId) {
		return await updateMessage()
	}

	if (props.noteId) {
		return await updateNote()
	}

	return await addMessage()
}

const onClick = async (): Promise<void> => {
	const isValid = await v$.value.$validate()

	if (isValid) {
		try {
			isSending.value = true

			const wasEdit = isEditMode.value
			const successText = editSuccessText.value

			await sendMessage()

			if (wasEdit) {
				showSnackbar(successText)
			}

			clearForm()
			emit('success')
		} finally {
			isSending.value = false
		}
	}
}

const onCancel = (): void => {
	clearForm()
	emit('cancel')
}

defineExpose({
	editor
})
</script>

<template>
  <div class="cp-chat-replay">
    <CPTextEditor
      ref="editor"
      v-model="message"
      :disabled="isSending || disabled"
      class="cp-chat-replay__replay-editor"
      placeholder="Geben Sie Ihre Nachricht ein"
      :error="v$.message.$error"
      :error-messages="getErrors(v$.message.$errors)"
      @input="v$.message.$touch"
      @keyup="onClick"
    />
    <div class="cp-chat-replay__bottom">
      <CPCheckbox
        v-if="showSendEmail"
        v-model="isSentToEmail"
        :disabled="disabled"
        label="Senden Sie diese Nachricht zusätzlich an die E-Mail-Adressen der Chatteilnehmer"
      />
      <div class="cp-chat-replay__actions">
        <CPButton
          v-if="isEditMode"
          color="neutral"
          variant="outlined"
          :disabled="disabled || isSending"
          @click="onCancel"
        >
          Abbrechen
        </CPButton>
        <CPButton
          color="main"
          :loading="isSending"
          @click="onClick"
          :disabled="disabled"
        >
          {{ buttonLabel }}
        </CPButton>
      </div>
      <div v-if="isFileEditable" class="cp-chat-replay__files">
        <CPFileUpload
          :key="uploadKey"
          v-model="files"
          :multiple="true"
          :delete-icon="true"
          :allow-download="false"
          :disabled="disabled"
          label="Dateien hochladen"
          accept="*"
          :max-size="50"
          @error="showSnackbar('Datei ist zu groß', 'cp-base-red')"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  .cp-chat-replay {
    @apply
      p-4
      grid
      gap-4
      sm:(
        p-6
      )
    ;

    &__files {
      @apply
        flex
        w-full
      ;
    }

    &__actions {
      @apply
        flex
        items-center
        gap-4
        md:(
          ml-a
          order-last
        )
      ;
    }

    &__replay-editor {
      .cp-text-editor__editor {
        @apply
          min-h-2
        ;
      }
    }

    &__bottom {
      @apply
        flex
        items-start
        flex-col
        flex-wrap
        gap-4
        md:(
          flex-row
          justify-between
          items-center
        )
      ;
    }
  }
</style>
